import React, { useCallback } from "react";
import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import PlatePlacement from "../../../components/PlatePlacement";
import SettingFields from "./SettingFields";

/**
 * The Substrate position card: where the substrate sits under the laser, and
 * the plate header.
 *
 * The offset (mm along the laser x / y axes, negative = hanging out of the
 * field) and the header choice are the page's; this card shows the placement
 * canvas for them and reports the server's layout (null while the plate lies
 * outside the field, or while the plate design is unusable) through
 * onLayoutChange. The canvas follows the page's plate design, so the slots
 * reflect the Substrate card's patch geometry; the header band's height is
 * the plate design's and is edited here, next to the header choice.
 */
const HEADER_HEIGHT_FIELD = {
  path: ["header", "header_height"],
  label: "Header height",
  min: 0,
  unit: "mm",
  help: "Band reserved at the top of the plate",
};

export default function PlatePositionCard({
  materialId,
  laserDeviceId,
  offset,
  markHeader,
  plateDesignConfig,
  onPlateDesignChange,
  onOffsetChange,
  onHeaderChange,
  onLayoutChange,
  onInvalidChange,
}) {
  const handleOffsetChange = useCallback(
    (next) => onOffsetChange({ x: next.x, y: next.y }),
    [onOffsetChange]
  );
  const designLoaded = Boolean(plateDesignConfig && Object.keys(plateDesignConfig).length > 0);

  if (!(materialId && laserDeviceId)) {
    return (
      <Typography variant="body2" color="text.secondary">
        Select a laser and a substrate first.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Drag the substrate to where it sits under the laser. Patch groups are laid out inside the
        marking field only.
      </Typography>
      <PlatePlacement
        materialId={materialId}
        laserDeviceId={laserDeviceId}
        plateDesignConfig={designLoaded ? plateDesignConfig : null}
        offset={offset}
        onOffsetChange={handleOffsetChange}
        onLayoutChange={onLayoutChange}
      />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-start" }}>
        <FormControlLabel
          control={<Checkbox checked={markHeader} onChange={(event) => onHeaderChange(event.target.checked)} />}
          label={<Typography variant="body2">Mark plate header</Typography>}
        />
        {markHeader && designLoaded ? (
          <Stack sx={{ width: { xs: "100%", sm: 240 } }}>
            <SettingFields
              config={plateDesignConfig}
              fields={[HEADER_HEIGHT_FIELD]}
              onChange={onPlateDesignChange}
              onInvalidChange={onInvalidChange}
              testPrefix="plate"
              columns={1}
            />
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
}
